import Table from '@cloudscape-design/components/table'
import Header from '@cloudscape-design/components/header'
import Box from '@cloudscape-design/components/box'
import type { ProfileStats, CallResult } from '@/types'
import { getLatencyColor } from '@/utils/latency-colors'

import {
  colorTextStatusSuccess,
  colorTextStatusWarning,
  colorTextStatusError,
} from '@cloudscape-design/design-tokens'

interface Props { stats: ProfileStats[]; results?: CallResult[] }

function getCrisLabel(results: CallResult[], profileId: string): string {
  const r = results.find(r => r.profileId === profileId)
  if (!r) return '-'
  if (r.inferenceProfileType === 'direct-api') return 'Direct API'
  if (r.inferenceProfileType === 'global') return 'Bedrock Global CRIS'
  if (r.inferenceProfileType === 'cross-region') return `Bedrock Cross-region (${r.inferenceProfileGeo || '?'})`
  if (r.apiEndpointRegion) return `Single (${r.apiEndpointRegion})`
  return '-'
}

/** Color-code a latency cell using thresholds (in the cell's native unit). */
function coloredCell(value: number, thresholds: [number, number], formatter: (v: number) => string) {
  // Use custom thresholds: green ≤ t[0], yellow ≤ t[1], red > t[1]
  const color = value <= thresholds[0] ? colorTextStatusSuccess : value <= thresholds[1] ? colorTextStatusWarning : colorTextStatusError
  return (
    <Box color="inherit">
      <span style={{ color, fontFamily: 'monospace' }}>{formatter(value)}</span>
    </Box>
  )
}

export function SummaryTable({ stats, results = [] }: Props) {
  if (stats.length === 0) return null

  return (
    <Table
      columnDefinitions={[
        { id: 'profile', header: 'Profile', cell: (s: ProfileStats) => s.profileName },
        { id: 'cris', header: 'CRIS / Region', cell: (s: ProfileStats) => (
          <Box fontSize="body-s" color="text-status-info">{getCrisLabel(results, s.profileId)}</Box>
        )},
        { id: 'calls', header: 'Calls', cell: (s: ProfileStats) => `${s.successCount}/${s.callCount}` },
        { id: 'avgTotal', header: 'Avg Total (ms)', cell: (s: ProfileStats) => coloredCell(s.avgTotalSec * 1000, [3000, 8000], v => String(Math.round(v))) },
        { id: 'p50', header: 'P50 (ms)', cell: (s: ProfileStats) => coloredCell(s.medianTotalSec * 1000, [3000, 8000], v => String(Math.round(v))) },
        { id: 'p75', header: 'P75 (ms)', cell: (s: ProfileStats) => coloredCell(s.p75TotalSec * 1000, [4000, 10000], v => String(Math.round(v))) },
        { id: 'p90', header: 'P90 (ms)', cell: (s: ProfileStats) => coloredCell(s.p90TotalSec * 1000, [5000, 12000], v => String(Math.round(v))) },
        { id: 'p95', header: 'P95 (ms)', cell: (s: ProfileStats) => coloredCell(s.p95TotalSec * 1000, [5000, 15000], v => String(Math.round(v))) },
        { id: 'p99', header: 'P99 (ms)', cell: (s: ProfileStats) => coloredCell(s.p99TotalSec * 1000, [10000, 30000], v => String(Math.round(v))) },
        { id: 'avgTtft', header: 'Avg TTFT (ms)', cell: (s: ProfileStats) => coloredCell(s.avgTtftSec * 1000, [2000, 5000], v => String(Math.round(v))) },
        { id: 'brTtft', header: 'BR TTFT (ms)', cell: (s: ProfileStats) => s.avgBedrockFirstByteMs > 0 ? Math.round(s.avgBedrockFirstByteMs) : '-' },
        { id: 'throughput', header: 'Throughput (tok/s)', cell: (s: ProfileStats) => `${s.avgThroughput.toFixed(1)} tok/s` },
        { id: 'inputTokens', header: 'Input Tokens', cell: (s: ProfileStats) => Math.round(s.avgInputTokens) },
        { id: 'outputTokens', header: 'Output Tokens', cell: (s: ProfileStats) => Math.round(s.avgOutputTokens) },
        { id: 'brInvoc', header: 'BR Invoc (ms)', cell: (s: ProfileStats) => s.avgBedrockInvocationMs > 0 ? Math.round(s.avgBedrockInvocationMs) : '-' },
        { id: 'grInput', header: 'Guardrail In (ms)', cell: (s: ProfileStats) => s.avgGuardrailInputMs > 0 ? Math.round(s.avgGuardrailInputMs) : '-' },
        { id: 'grOutput', header: 'Guardrail Out (ms)', cell: (s: ProfileStats) => s.avgGuardrailOutputMs > 0 ? Math.round(s.avgGuardrailOutputMs) : '-' },
        { id: 'grTotal', header: 'Guardrail Total (ms)', cell: (s: ProfileStats) => {
          if (s.avgGuardrailTotalMs <= 0) return '-'
          return coloredCell(s.avgGuardrailTotalMs, [300, 700], v => String(Math.round(v)))
        }},
        { id: 'e2eTotal', header: 'E2E Total (ms)', cell: (s: ProfileStats) => {
          if (s.avgE2eTotalSec <= 0) return '-'
          return coloredCell(s.avgE2eTotalSec * 1000, [5000, 15000], v => String(Math.round(v)))
        }},
        { id: 'netOverhead', header: 'Net Overhead (ms)', cell: (s: ProfileStats) => {
          const overhead = s.avgBedrockInvocationMs > 0
            ? s.avgTotalSec * 1000 - s.avgBedrockInvocationMs
            : (s.avgE2eTotalSec > 0 && s.avgTotalSec > 0 ? (s.avgE2eTotalSec - s.avgTotalSec) * 1000 : 0)
          if (overhead === 0 && s.avgBedrockInvocationMs <= 0 && !(s.avgE2eTotalSec > 0 && s.avgTotalSec > 0)) return '-'
          return coloredCell(overhead, [100, 300], v => String(Math.round(v)))
        }},
      ]}
      items={stats}
      header={<Header>Summary Statistics</Header>}
      stripedRows
      sortingDisabled={false}
    />
  )
}
